import {Component, computed, inject, input} from '@angular/core';
import {AppNumberPipe} from '@frontend/shared';
import {CommonModule} from '@angular/common';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {ParticipatingToolResponse} from '@frontend/shared-api';
import {DirectusTranslationPipe, HrTool} from '@frontend/directus';
import {
  getMaxScore,
  getThresholdScore,
} from '../../../../../../../../shared/utils/rfd-evaluation-score.utils';
import {EnrichedEvaluationDataResponse} from '../../evaluation-models';
import {
  RfdProviderScore,
  RfdScoreCalculationService,
} from '../../../../../../../../shared/services/rfd-score-calculation.service';

@Component({
  selector: 'app-evaluation-summary',
  imports: [AppNumberPipe, CommonModule, TranslocoPipe, DirectusTranslationPipe],
  templateUrl: './evaluation-summary.component.html',
})
export class EvaluationSummaryComponent {
  readonly data = input.required<EnrichedEvaluationDataResponse>();
  readonly hrToolsMap = input<Map<string, HrTool>>(new Map<string, HrTool>());
  readonly averageUserIds = input<number[]>([]);
  readonly isExponentialScale = input(false);

  private readonly transloco = inject(TranslocoService);
  private readonly rfdScoreCalculationService = inject(RfdScoreCalculationService);
  protected readonly currentLang = computed(() => this.transloco.getActiveLang());

  protected providerScores = computed(() =>
    this.rfdScoreCalculationService.calculateProviderScores(
      this.data(),
      this.averageUserIds(),
      this.isExponentialScale(),
    ),
  );
  protected leader = computed(() => {
    const scores = this.providerScores();
    if (scores.length === 0) return null;

    const sorted = [...scores].sort((a, b) => b.overall - a.overall);
    const top = sorted[0];
    if (top.overall === 0 && top.subcriteriaScores.size === 0) return null;

    const tool = (this.data().tools ?? []).find(
      (t: ParticipatingToolResponse) => t.id === top.toolId,
    );
    const hrTool = tool ? this.hrToolsMap().get(tool.hrToolDirectusId) : undefined;
    return hrTool?.name ?? tool?.hrToolDirectusId ?? null;
  });

  expanded = true;

  getProviderName(toolId: number): string {
    const tool = (this.data().tools ?? []).find((t: ParticipatingToolResponse) => t.id === toolId);
    const hrTool = tool ? this.hrToolsMap().get(tool.hrToolDirectusId) : undefined;
    return hrTool?.toolProvider?.name ?? '';
  }

  getHrToolName(toolId: number): string {
    const tool = (this.data().tools ?? []).find((t: ParticipatingToolResponse) => t.id === toolId);
    const hrTool = tool ? this.hrToolsMap().get(tool.hrToolDirectusId) : undefined;
    return hrTool?.name ?? tool?.hrToolDirectusId ?? '';
  }

  getMaxScore(): number {
    return getMaxScore(this.isExponentialScale());
  }

  getThresholdScore(starValue: number): number {
    return getThresholdScore(starValue, this.isExponentialScale());
  }

  getOverallBreakdown(score: RfdProviderScore) {
    return (this.data().subcriteria ?? []).map((subcriteria) => {
      const subcriteriaScore = score.subcriteriaScores.get(subcriteria.id) ?? 0;
      const weight = subcriteria.weight ?? 0;
      return {
        subcriteria,
        weight,
        score: subcriteriaScore,
        contribution: subcriteriaScore * weight,
      };
    });
  }
}
